export const DashboardWidgetId = {
    Flights: 'FLIGHTS',
    Regularity: 'REGULARITY',
    Weather: 'WEATHER',
    WeatherForecast: 'WEATHER_FORECAST',
    Events: 'EVENTS',
    Runways: 'RUNWAYS',
    NewsFeed: 'NEWS_FEED',
    Pax: 'PAX',
    PaxForecast: 'PAX_FORECAST',
    Delays: 'DELAYS',
    Punctuality: 'PUNCTUALITY',
    Cdm: 'CDM',
    BaggageDelivery: 'BAGGAGE_DELIVERY',
    OperationalForecast: 'OPERATIONAL_FORECAST',
    QueueingTimeBorder: 'QUEUEING_TIME_BORDER',
    QueueingTimeSecurity: 'QUEUEING_TIME_SECURITY',
    FireFighters: 'Firefighters',
    ShiftNotes: 'ShiftNotes',
    Restrictions: 'RESTRICTIONS',
    RecentTasks: 'RECENT_TASKS',
    UpcomingTasks: 'UPCOMING_TASKS',
    FOD: 'FOD',
    NOTAM: 'NOTAM',
} as const

export type DashboardWidgetId = (typeof DashboardWidgetId)[keyof typeof DashboardWidgetId]

export const RunwaysOption = {
    ClosurePlan: 'closurePlan',
    Metar: 'metar',
} as const

export const WeatherOption = {
    NetworkAirportProbabilityBars: 'networkAirportProbabilityBars',
} as const

export const CdmOption = {
    TobtGauge: 'tobtGauge',
    TobtChart: 'tobtChart',
    DeicingGauge: 'deicingGauge',
    TimePeriodTop5: 'timePeriodTop5',
    TimePeriodLast24h: 'timePeriodLast24h',
    TimePeriodFuture: 'timePeriodFuture',
} as const

export type DashboardPlatform = 'web' | 'mobile'

export type WebWidgetView = 'tile' | 'full'

export interface WidgetToggleOptionDefinition {
    kind: 'toggle'
    key: string
    title: string
    description: string
}

export type WidgetOptionDefinition = WidgetToggleOptionDefinition

export interface WidgetDefinition {
    id: DashboardWidgetId
    title: string
    description: string
    platforms: DashboardPlatform[]
    webViews: WebWidgetView[]
    options: WidgetOptionDefinition[]
}

const BOTH: DashboardPlatform[] = ['web', 'mobile']
const BOTH_VIEWS: WebWidgetView[] = ['tile', 'full']

const toggle = (key: string, title: string, description: string): WidgetToggleOptionDefinition => ({
    kind: 'toggle',
    key,
    title,
    description,
})

export const DASHBOARD_WIDGETS: readonly WidgetDefinition[] = [
    {
        id: DashboardWidgetId.Flights,
        title: 'Flights',
        description: 'Arrival and departure counts.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Regularity,
        title: 'Regularity',
        description: 'Share of flights operated as scheduled.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Weather,
        title: 'Weather',
        description: 'Current conditions and the weather chart.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [
            toggle(
                WeatherOption.NetworkAirportProbabilityBars,
                'Probability bars at network airports',
                'Thunderstorm and other probability bars below the weather chart at network airports. The main airport always shows them.',
            ),
        ],
    },
    {
        id: DashboardWidgetId.WeatherForecast,
        title: 'Weather forecast',
        description: 'Forecast for the coming hours.',
        platforms: ['web'],
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Events,
        title: 'Events',
        description: 'Active events at the airport.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Runways,
        title: 'Runways',
        description: 'Runway modes and status.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [
            toggle(
                RunwaysOption.ClosurePlan,
                'Closure plan',
                'Planned runway closures and the closure carousel.',
            ),
            toggle(RunwaysOption.Metar, 'METAR', 'Latest METAR message below the runways.'),
        ],
    },
    {
        id: DashboardWidgetId.NewsFeed,
        title: 'News feed',
        description: 'Latest news items.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Pax,
        title: 'Passengers',
        description: 'Passenger counts.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.PaxForecast,
        title: 'Passenger forecast',
        description: 'Forecasted passenger volumes.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Delays,
        title: 'Delays',
        description: 'Delayed flights and delay reasons.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Punctuality,
        title: 'Punctuality',
        description: 'On-time performance.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.Cdm,
        title: 'CDM',
        description: 'Collaborative decision making metrics.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [
            toggle(CdmOption.TobtGauge, 'TOBT gauge', 'The TOBT gauge.'),
            toggle(CdmOption.TobtChart, 'TOBT chart', 'Hourly TOBT chart.'),
            toggle(CdmOption.DeicingGauge, 'De-icing gauge', 'The de-icing gauge.'),
            toggle(
                CdmOption.TimePeriodTop5,
                'Time period: top 5',
                'Top 5 option in the gauge time period picker.',
            ),
            toggle(
                CdmOption.TimePeriodLast24h,
                'Time period: last 24 h',
                'Last 24 hours option in the gauge time period picker.',
            ),
            toggle(
                CdmOption.TimePeriodFuture,
                'Time period: future',
                'Future option in the gauge time period picker.',
            ),
        ],
    },
    {
        id: DashboardWidgetId.BaggageDelivery,
        title: 'Baggage delivery',
        description: 'Baggage delivery times.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.OperationalForecast,
        title: 'Operational forecast',
        description: 'Daily operations forecast.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.QueueingTimeBorder,
        title: 'Queueing time: border',
        description: 'Border control queueing times.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.QueueingTimeSecurity,
        title: 'Queueing time: security',
        description: 'Security control queueing times.',
        platforms: BOTH,
        webViews: BOTH_VIEWS,
        options: [],
    },
    {
        id: DashboardWidgetId.FireFighters,
        title: 'Firefighters',
        description: 'Firefighter shift status.',
        platforms: BOTH,
        webViews: ['full'],
        options: [],
    },
    {
        id: DashboardWidgetId.ShiftNotes,
        title: 'Shift notes',
        description: 'Notes handed over between shifts.',
        platforms: BOTH,
        webViews: ['full'],
        options: [],
    },
    {
        id: DashboardWidgetId.Restrictions,
        title: 'Restrictions',
        description: 'Active stand and area restrictions.',
        platforms: BOTH,
        webViews: ['tile'],
        options: [],
    },
    {
        id: DashboardWidgetId.RecentTasks,
        title: 'Recent tasks',
        description: 'Recently updated tasks.',
        platforms: ['web'],
        webViews: ['full'],
        options: [],
    },
    {
        id: DashboardWidgetId.UpcomingTasks,
        title: 'Upcoming tasks',
        description: 'Tasks due soon.',
        platforms: ['web'],
        webViews: ['full'],
        options: [],
    },
    {
        id: DashboardWidgetId.FOD,
        title: 'FOD',
        description: 'Foreign object debris reports.',
        platforms: ['mobile'],
        webViews: [],
        options: [],
    },
    {
        id: DashboardWidgetId.NOTAM,
        title: 'NOTAM',
        description: 'Active notices to airmen.',
        platforms: BOTH,
        webViews: ['tile'],
        options: [],
    },
]

export const getWidgetDefinition = (id: DashboardWidgetId): WidgetDefinition | undefined =>
    DASHBOARD_WIDGETS.find(widget => widget.id === id)
